import { upsertUser } from '@/services/users';
import { client } from '@/services/amplifyClient';
import {
    fetchAuthSession,
    fetchUserAttributes,
    getCurrentUser,
    signOut,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useCallback, useEffect, useState } from 'react';

// Mock user for development/demo purposes
const MOCK_USER = {
  id: 'mock-user-123',
  email: 'demo@sunsquatch.app',
  name: 'Demo User',
  preferredUsername: 'demo_user',
  profilePicture: undefined,
};

// Enable mock auth for testing (can be toggled via localStorage)
const MOCK_AUTH_KEY = 'sunsquatch-mock-auth';
// Only allow demo mode in development environments
const ALLOW_DEMO_MODE = import.meta.env.DEV;

interface User {
  id: string;
  email: string;
  name?: string;
  profilePicture?: string;
  preferredUsername?: string;
  onboardingComplete?: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const loadUser = useCallback(async () => {
    // Check for mock auth first (demo mode)
    const isMockAuth = ALLOW_DEMO_MODE && localStorage.getItem(MOCK_AUTH_KEY) === 'true';
    if (isMockAuth) {
      // Skip upsertUser for mock users - they don't have valid auth tokens
      setState({
        user: MOCK_USER,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      return;
    }

    try {
      const authUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();

      const user: User = {
        id: authUser.userId,
        email: attributes.email || '',
        name: attributes.name || attributes.preferred_username,
        preferredUsername: attributes.preferred_username,
        profilePicture: attributes.picture,
      };

      void upsertUser(user);

      // Fetch full user record to get onboardingComplete status
      try {
        const { data: fullUser } = await client.models.User.get({ id: authUser.userId });
        if (fullUser) {
          user.onboardingComplete = fullUser.onboardingComplete ?? false;
        }
      } catch {
        // Non-fatal; default to false so user can go through onboarding
        // Could not load full user record for onboarding status
        user.onboardingComplete = false;
      }

      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch {
      // User is not authenticated
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  }, []);

  useEffect(() => {
    loadUser();

    // Listen for auth events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          loadUser();
          break;
        case 'signedOut':
          setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
          break;
        case 'tokenRefresh':
          loadUser();
          break;
        case 'tokenRefresh_failure':
          setState(prev => ({
            ...prev,
            error: new Error('Token refresh failed'),
          }));
          break;
      }
    });

    return () => unsubscribe();
  }, [loadUser]);

  const logout = useCallback(async () => {
    try {
      // Clear mock auth if set
      localStorage.removeItem(MOCK_AUTH_KEY);
      await signOut();
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch {
      // Even if signOut fails (e.g., no real user), clear state
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  }, []);

  // Mock login for demo purposes
  const mockLogin = useCallback(() => {
    if (!ALLOW_DEMO_MODE) {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: new Error('Demo login is not available'),
      });
      return;
    }

    localStorage.setItem(MOCK_AUTH_KEY, 'true');
    // Skip upsertUser for mock users - they don't have valid auth tokens
    setState({
      user: MOCK_USER,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
  }, []);

  const getAccessToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString();
    } catch {
      return null;
    }
  }, []);

  return {
    ...state,
    logout,
    mockLogin,
    getAccessToken,
    refreshUser: loadUser,
    isDemoModeAvailable: ALLOW_DEMO_MODE,
    isDemoMode: ALLOW_DEMO_MODE && state.user?.id === MOCK_USER.id,
  };
}
